import React, { Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";
import { Filter, DefaultColumnFilter } from "./filters";
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter";

export const DEFAULT_SORTBY = [
  {
    desc: true,
  },
];

// Define a default UI for filtering
function GlobalFilter({ searchText, onSearchChange, isJobListGlobalFilter }) {
  const [value, setValue] = React.useState(searchText || "");
  const onChange = useAsyncDebounce((val) => {
    onSearchChange(val);
  }, 300);

  return (
    <React.Fragment>
      <div className="col-md-4">
        <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={(e) => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`Search...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </div>
      {isJobListGlobalFilter && <JobListGlobalFilter />}
    </React.Fragment>
  );
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  isAddUserList,
  handleAddClick,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  customPageSize,
  className,
  customPageSizeOptions,
  controlledPagination,
  onSortChange,
  searchText,
  onSearchChange,
  addOptionTitle,
  titleComponents = []
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    pageOptions,
    gotoPage,
    state: { sortBy, ...state },
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 1,
        pageSize: customPageSize,
        sortBy: DEFAULT_SORTBY,
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPagination?.totalPages || 0,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  useEffect(() => {
    if (onSortChange) onSortChange(sortBy);
  }, [sortBy]);

  const { pageIndex, setPageIndex, pageSize, setPageSize, totalPages } =
    controlledPagination || {};

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };

  const onChangeInInput = (event) => {
    const page = event.target.value ? Math.max(Number(event.target.value), 1) : 1;
    gotoPage(page);
  };

  return (
    <Fragment>
      <div className="row mb-2">
        <div className={customPageSizeOptions ? "col-md-2" : "col-md-1"}>
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPageIndex(1); // Reset to first page
            }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
            searchText={searchText}
            onSearchChange={onSearchChange}
          />
        )}
        <div className="flex-1"></div>
        <div className="col-sm-6">
          <div className="text-sm-end">
            {isAddOptions && (
              <button
                type="button"
                className="btn btn-success btn-rounded mb-2 me-2"
                onClick={handleAddClick}
              >
                <i className="mdi mdi-plus me-1" />
                {addOptionTitle}
              </button>
            )}
            {titleComponents.map((Component, idx) => (
              <Component key={idx} />
            ))}
          </div>
        </div>
        {isAddUserList && (
          <div className="col-sm-7">
            <div className="text-sm-end">
              <button
                type="button"
                className="btn btn-primary mb-2 me-2"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </button>
            </div>
          </div>
        )}
        {isAddCustList && (
          <div className="col-sm-7">
            <div className="text-sm-end">
              <button
                type="button"
                className="btn btn-success btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="table-responsive react-table">
        <table className={`table table-bordered table-hover ${className}`} {...getTableProps()}>
          <thead className="table-light table-nowrap" key={Date.now()}>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } =
                      column.getHeaderProps();
                    return (
                      <th key={colKey} {...colProps}>
                        <div
                          className="mb-2"
                          {...column.getSortByToggleProps()}
                        >
                          {column.render("Header")}
                          {generateSortingIndicator(column)}
                        </div>
                        <Filter column={column} />
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key, ...rowProps } = row.getRowProps();
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr key={key} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key, ...cellProps } = cell.getCellProps();
                      return (
                        <td key={key} {...cellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="row justify-content-md-end justify-content-center align-items-center">
        <div className="col-md-auto">
          <div className="d-flex gap-1">
            <button
              className="btn btn-primary"
              onClick={() => setPageIndex(1)}
              disabled={pageIndex === 1}
            >
              {"<<"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
              disabled={pageIndex === 1}
            >
              {"<"}
            </button>
          </div>
        </div>

        <div className="col-md-auto d-none d-md-block">
          Page{" "}
          <strong>
            {pageIndex} of {pageOptions.length}
          </strong>
        </div>
        <div className="col-md-auto">
          <input
            type="number"
            min={1}
            style={{ width: 70 }}
            max={pageOptions.length}
            defaultValue={pageIndex}
            onChange={onChangeInInput}
            className="form-control"
          />
        </div>

        <div className="col-md-auto">
          <div className="d-flex gap-1">
            <button
              className="btn btn-primary"
              onClick={() => setPageIndex((prev) => prev + 1)}
              disabled={pageIndex === totalPages}
            >
              {">"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setPageIndex(totalPages)}
              disabled={pageIndex === totalPages}
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;